import CartPicker from '~/src/common/components/CartPicker/CartPicker';
import { useUpdateCartProductQuantity } from '~/src/common/hooks/cart/useUpdateCartProductQuantity';
import { useDelivery } from '~/src/common/hooks/useDelivery';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';
import { useATCSourceFromWithWhitelist } from '~/src/screens/CatalogContent/ContentScreen/hooks/useATCSourceFrom';
import ArticleItem from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/components/ArticleItem';
import { GenericArticleItem } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/components/types';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import { ATCSource, ATCSourceFrom } from '~/src/screens/CatalogContent/ContentScreen/types';

import * as S from './layout';

interface Props {
  article: GenericArticleItem;
  recipeId: string;
}

const whitelistSourceFrom = [
  'cart',
  'products list',
  'search results',
  'recipe page cross sell',
  'recipes list',
] satisfies ATCSourceFrom['type'][];

const ArticleItemCell = ({ article, recipeId }: Props) => {
  const { categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();

  const { id, availableQuantity, packSize } = article;

  const from = useATCSourceFromWithWhitelist(whitelistSourceFrom);
  const source: ATCSource = {
    type: 'essentials recipe ingredients list',
    detail: {
      recipeId,
      from,
    },
  };

  const { mutate: updateQuantity } = useUpdateCartProductQuantity({
    product: article,
    source,
    categoryName,
    subcategoryName,
  });

  const handleUpdateQuantity = (quantity: number) => {
    updateQuantity({
      data: {
        product: { id, quantity },
      },
    });
  };

  const { data: cart } = useGetCart();

  const cartQuantity = cart?.products?.find(p => p.id === id)?.quotation2.count.quantity ?? 0;

  const deliveryAddressModal = useNiceModal('delivery-address-modal');
  const deliverySelectSlotModal = useNiceModal('delivery-select-slot-modal');
  const delivery = useDelivery();

  const isAddressSetup = delivery.address != null;
  const canUpdateCart = isAddressSetup && delivery.isTimeSlotValid;

  const isAvailable = availableQuantity >= packSize;

  const { show: showSubstituteProductsModal } = useNiceModal('substitute-products-modal');

  const handleDisabledClick = () => {
    if (isAddressSetup) {
      void deliverySelectSlotModal.show();
    } else {
      void deliveryAddressModal.show();
    }
  };

  const handleReplaceClick = () => {
    void showSubstituteProductsModal({ productSlug: article.slug });
  };

  return (
    <S.ArticleItemCellContainer key={id}>
      <ArticleItem article={article} source={source} onReplace={handleReplaceClick} />
      <S.CartPickerContainer>
        {isAvailable ? (
          <CartPicker
            product={article}
            availableQuantity={availableQuantity}
            cachedQuantity={cartQuantity}
            onUpdateCartQuantity={handleUpdateQuantity}
            enabled={canUpdateCart}
            onDisabledClick={handleDisabledClick}
          />
        ) : null}
      </S.CartPickerContainer>
    </S.ArticleItemCellContainer>
  );
};

export default ArticleItemCell;
